import type { SurfaceSolutionTag } from "../api/types";

export type MergeTagIdsOptions = {
  /** Selected temporal phase tag; replaces any existing temporal ids. */
  temporalTagId?: number | null;
  /** All temporal tag ids for the board (so we can strip the old phase). */
  temporalTagIds?: number[] | null;
};

/**
 * Build accept `tag_ids`: keep other non-solution tags, replace solution ids,
 * optionally replace temporal with `temporalTagId`.
 */
export function mergeTagIds(
  existingTagIds: number[] | null | undefined,
  selectedSolutionIds: number[] | null | undefined,
  allTags: SurfaceSolutionTag[] | null | undefined,
  options?: MergeTagIdsOptions
): number[] {
  const existing = existingTagIds ?? [];
  const selected = selectedSolutionIds ?? [];
  const tags = allTags ?? [];

  const solutionIdSet = new Set(
    tags.filter((t) => t.is_solution !== false).map((t) => t.id)
  );
  const temporalIdSet = new Set(
    (options?.temporalTagIds ?? []).filter((id) => Number.isFinite(id))
  );

  const preserved = existing.filter(
    (id) => !solutionIdSet.has(id) && !temporalIdSet.has(id)
  );
  const uniqueSelected = Array.from(new Set(selected.filter((id) => Number.isFinite(id))));

  const temporalId = options?.temporalTagId;
  const temporal =
    temporalId != null && Number.isFinite(temporalId) ? [temporalId] : [];

  return Array.from(new Set([...preserved, ...uniqueSelected, ...temporal]));
}

/** Prefer `tag_ids`; fall back to legacy `tags` array on some payloads. */
export function assetTagIds(asset: {
  tag_ids?: number[] | null;
  tags?: number[] | null;
}): number[] {
  return asset.tag_ids ?? asset.tags ?? [];
}

/** Shared accept-form gates for overlay + bulk sheet. */
export function getAcceptReadiness(input: {
  solutionTagIds: number[];
  temporalTagId: number | null;
  temporalTagCount: number;
}): { needsSolutionTag: boolean; needsTemporalTag: boolean; canAccept: boolean } {
  const needsSolutionTag = input.solutionTagIds.length < 1;
  const needsTemporalTag = input.temporalTagCount > 0 && input.temporalTagId == null;
  return {
    needsSolutionTag,
    needsTemporalTag,
    canAccept: !needsSolutionTag && !needsTemporalTag,
  };
}
